import { retrieveLaunchParams, retrieveRawInitData } from "@telegram-apps/sdk-react";

/**
 * Telegram Mini App auth primitives — the client half of the silent
 * `initData → JWT` exchange (#226 Phase 1).
 *
 * In a real TG WebView the launch URL carries a signed `initData` string that
 * the backend HMAC-verifies; we never see a password. Outside Telegram (plain
 * browser preview, `bun dev` at :5173) `retrieveRawInitData()` throws, so every
 * accessor is wrapped to degrade to `null` rather than crash the boot path.
 */

/** Raw, unmodified `initData` string for the backend, or null outside TG. */
export function getRawInitData(): string | null {
  try {
    const raw = retrieveRawInitData();
    return raw && raw.length > 0 ? raw : null;
  } catch {
    // Not running inside a Telegram client (browser preview / SSR / tests).
    return null;
  }
}

/**
 * Invite code for the silent-login body, best-effort.
 *
 * Two sources, in priority order:
 *  1. Telegram deep-link `start_param` (`t.me/kupikupi_bot/app?startapp=<code>`)
 *     — the native Mini App referral carrier.
 *  2. `localStorage["kupikupi-invite-code"]` — the shared web/mini key (#241).
 *
 * NOTE (#241 coordination): invite-code *lifecycle* (URL capture, register
 * prefill, post-login clear) is owned by #241's `kk-auth-group`. This reader is
 * deliberately read-only and does not write/clear the key, so it can coexist
 * with that work. The backend binds this field only when Mini App auto-registers
 * a new Telegram user.
 */
export function getInviteCode(): string | undefined {
  // 1. Telegram start_param.
  try {
    const lp = retrieveLaunchParams();
    const startParam = lp.tgWebAppStartParam;
    if (typeof startParam === "string" && startParam.trim()) return startParam.trim();
  } catch {
    // not in TG env
  }
  // 2. Shared localStorage key.
  try {
    if (typeof window !== "undefined") {
      const stored = window.localStorage.getItem("kupikupi-invite-code");
      if (stored?.trim()) return stored.trim();
    }
  } catch {
    // storage blocked
  }
  return undefined;
}
